import React from 'react';
import { Container, Typography, Box, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { ContactInfoImage } from './ContactInfoImage';
import { SEO } from './SEO';

const contentUseCase = new ContentUseCase();

export const ImpressumPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const content = contentUseCase.getImpressum(language);

  return (
    <Container maxWidth="xl" sx={{ py: theme.spacing(6) }}>
      <SEO page="impressum" />
      <Typography variant="h2" component="h1" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
          {content.title}
        </Typography>

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.contact.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.contact.description}
          </Typography>
          <ContactInfoImage language={language} />
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.purpose.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.purpose.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.purpose.goal}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.shirts.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.shirts.description}
          </Typography>
          {[
            content.shirts.price,
            content.shirts.contract,
            content.shirts.withdrawal,
            content.shirts.withdrawalConsequences,
            content.shirts.warranty,
            content.shirts.dispute,
          ].map((section) => (
            <Typography key={section.title} variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
              <strong>{section.title}</strong> {section.text}
            </Typography>
          ))}
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.disclaimer.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.disclaimer.content.title}</strong> {content.disclaimer.content.text}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.disclaimer.external.title}</strong> {content.disclaimer.external.text}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.disclaimer.donations.title}</strong> {content.disclaimer.donations.text}
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.copyright.title}
          </Typography>
          <Typography variant="h6" paragraph color="text.primary" align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            {content.copyright.description}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <strong>{content.copyright.images}</strong>
          </Typography>
        </Box>

        <Box sx={{
          width: '100px',
          height: '2px',
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: theme.spacing(8),
        }} />

        <Box sx={{ mb: theme.spacing(8) }}>
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(4) }}>
            {content.additional.title}
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ maxWidth: { xs: '100%', md: 1000 }, mx: 'auto' }}>
            <Link href="/privacy" color="primary" underline="hover">
              {language === 'en' ? 'Privacy Policy' : 'Datenschutzerklärung'}
            </Link>
            {' | '}
            <Link href="/cookies" color="primary" underline="hover">
              {language === 'en' ? 'Cookie Policy' : 'Cookie-Erklärung'}
            </Link>
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {language === 'en' ? 'Last updated:' : 'Stand:'} {new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE')}
          </Typography>
        </Box>
      </Container>
  );
};
